<template>
    <TrialBar/>
    <div :class="[
        'overflow-x-hidden main-container',
        isInterviewSimulatorPage ? 'bg-[#09101D] dark:bg-[#09101D]' : 'bg-[#EDEDED] dark:bg-darkblue'
    ]">
        <TransitionRoot as="template" :show="sidebarOpen">
            <Dialog
                as="div"
                class="fixed inset-0 z-50 flex md:hidden"
                @close="sidebarOpen = false"
            >


                <TransitionChild
                    as="template"
                    enter="transition-opacity ease-linear duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />
                </TransitionChild>
                <TransitionChild
                    as="div"
                    class="flex flex-1"
                    enter="transition ease-in-out duration-300 transform"
                    enter-from="-translate-x-full"
                    enter-to="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leave-from="translate-x-0"
                    leave-to="-translate-x-full"
                >
                    <div class="relative w-full max-w-xs">
                        <TransitionChild
                            as="template"
                            enter="ease-in-out duration-300"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="ease-in-out duration-300"
                            leave-from="opacity-100"
                            leave-to="opacity-0"
                        >
                            <div
                                class="absolute left-full top-0 flex w-16 justify-center pt-1"
                            >
                                <button
                                    type="button"
                                    class="p-2.5"
                                    @click="sidebarOpen = false"
                                >
                                    <span class="sr-only">Close sidebar</span>
                                    <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                                </button>
                            </div>
                        </TransitionChild>
                        <Sidebar class="relative h-full w-full" />
                    </div>
                </TransitionChild>
                <div class="w-14 flex-shrink-0">
                    <!-- Force sidebar to shrink to fit close icon -->
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- Static sidebar for desktop -->
        <div
            v-if="sidebarExpanded"
            class="hidden md:fixed md:inset-y-0 md:z-10 md:flex md:w-64 md:flex-col"
            data-aos="fade-right"
        >
            <Sidebar
                :class="sidebarExpanded ? 'w-96' : 'w-16'"
                class="min-h-0 transition-all duration-300" @sidebar-toggle="toggleSidebar"
                :is-desktop="true"
            />
        </div>

        <div :style="sidebarExpanded && !isMobile ? { paddingLeft: '9rem' } : ''" class="flex flex-1 flex-col">
            <div
                class="z-40 flex w-full items-center justify-between border-b border-gray-200 bg-white px-4 pt-1 sm:px-6 md:hidden"
            >
                <div class="p-4" />
                <Logo class="mx-auto" width="200" height="100" />
                <button
                    type="button"
                    class="focus:ring-primary-500 -ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset"
                    @click="sidebarOpen = true"
                >
                    <span class="sr-only">{{ $t("craftable-pro", "Open sidebar") }}</span>
                    <Bars3Icon class="h-6 w-6" aria-hidden="true" />
                </button>

            </div>
            <main class="flex min-h-screen flex-1 flex-col" data-aos="fade-right" id="main-content-body">
                <slot>
                    <div class="mx-auto max-w-screen-2xl px-4 sm:px-6 md:px-8 bg-red">
                        <div
                            class="flex h-96 items-center justify-center rounded-lg border-4 border-dashed border-gray-200 p-4"
                        >
                          <span class="text-xl italic text-gray-300">
                            {{ $t("craftable-pro", "Your content goes here...") }}
                          </span>
                        </div>
                    </div>
                </slot>
            </main>
        </div>
    </div>

    <TwoFactorAuthenticationNoticeModal :open="twoFactorAuthenticationNoticeModalOpened" @toggle-open="twoFactorAuthenticationNoticeModalOpened = !twoFactorAuthenticationNoticeModalOpened">

    </TwoFactorAuthenticationNoticeModal>

    <PlanNoticeModal :open="planCTAModalOpened" @toggle-open="planCTAModalOpened = !planCTAModalOpened">

    </PlanNoticeModal>

    <!-- Client setup loading removido: fluxo de pack gerencia estado internamente -->
</template>

<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {ArrowRightIcon, Bars3Icon, XMarkIcon} from "@heroicons/vue/24/outline";
import Sidebar from "../craftable-pro/Components/Sidebar.vue";
import Logo from "@/craftable-pro/Components/Logo.vue";
import {router, usePage} from "@inertiajs/vue3";
import TwoFactorAuthenticationNoticeModal from "craftable-pro/Pages/Auth/TwoFactorAuthenticationNoticeModal.vue";
import IconButton from "../craftable-pro/Components/Button/IconButton.vue";
import AOS from 'aos';
import 'aos/dist/aos.css';
import PlanNoticeModal from "@/craftable-pro/Pages/Auth/PlanNoticeModal.vue";
import {useSettings} from "@/hooks/useSettings";
import {useOnboarding} from "@/Composables/useOnboarding";
import TrialBar from "@/craftable-pro/Components/Bar/TrialBar.vue";


AOS.init({
    duration: 400,
    easing: 'linear',
    once: false,
});

const sidebarOpen = ref(false);
const sidebarExpanded = ref(true);

const twoFactorAuthenticationNoticeModalOpened = ref<boolean>(false);
const planCTAModalOpened = ref<boolean>(false);

const page = usePage()

const isInterviewSimulatorPage = computed(() => page.component === 'InterviewSimulator/Index');

const showTwoFactorCTA = computed(() => !!(page.props.auth.showTwoFactorCTA));
const showPlanCTA = computed(() => !!(page.props.auth.showPlanCTA));

const isNotCheckoutPage = computed(() => {
    return !window.location.pathname.includes('checkout');
});

onMounted(() => {
    planCTAModalOpened.value = showPlanCTA.value;
    twoFactorAuthenticationNoticeModalOpened.value = showTwoFactorCTA.value;
    window.addEventListener('resize', handleResize);

    if (isProduction.value) {
        if (!document.getElementById('smartlook-script')) {
            const script = document.createElement('script');
            script.id = 'smartlook-script'; // Adiciona um ID para evitar duplicação
            script.type = 'text/javascript';
            script.innerHTML = `
                window.smartlook||(function(d) {
                    var o=smartlook=function(){ o.api.push(arguments)},h=d.getElementsByTagName('head')[0];
                    var c=d.createElement('script');o.api=new Array();c.async=true;c.type='text/javascript';
                    c.charset='utf-8';c.src='https://web-sdk.smartlook.com/recorder.js';h.appendChild(c);
                })(document);
                smartlook('init', '29f9c1e0538d12e12a470c80467fd3bfd9bcd2c7', { region: 'eu' });
            `;
            document.head.appendChild(script);
        }

    }
})

const toggleSidebar = () => {
    sidebarExpanded.value = !sidebarExpanded.value;
};

router.on("success", (event) => {
    if (sidebarOpen.value) {
        sidebarOpen.value = false;
    }
});

const windowWidth = ref(window.innerWidth)

const handleResize = () => {
  windowWidth.value = window.innerWidth
}

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

const isMobile = computed<boolean>(() => {
  return windowWidth.value < 768;
})

const {isProduction} = useSettings();

const { isTourActive, currentStep } = useOnboarding();

watch([isTourActive, currentStep], () => {
    // A lógica interna permanece a mesma, mas agora ela é acionada no momento certo.
    if (isTourActive.value && isMobile.value) {

        const sidebarTourTargets = [
            '#tour-step-1-dashboard', // Dica 1
            '#tour-step-4-sidebar',   // Dica 4
            '#tour-step-5-radar'    // Dica 5
        ];

        if (currentStep.value && sidebarTourTargets.includes(currentStep.value.target)) {
            sidebarOpen.value = true;
        }
    }
}, {
    deep: true
});

</script>

<style lang="scss">
header {
  @apply dark:bg-darkblue dark:text-white;

    h1, h2, h3, h4, h5, p, span {
         @apply dark:text-white
    }
}

div.rounded-md bg-white shadow {
  @apply dark:bg-darkblue dark:text-white;

    h1, h2, h3, h4, h5, p, span {
         @apply dark:text-white
    }
}

</style>
